/*
 * product-configurator-progress.js — list-type accordion cascade +
 * page-wide single-open + "all selections made" signal for the
 * Heirest-style PDP configurator.
 *
 * Figma dev annotation (node 11012:185341, "PDP/ all selections made"):
 *   "Exception to the rule of one accordion open at a time. If the
 *   selector is a list type, once the list item has been clicked, the
 *   accordion can close and the next one open. When the last selection
 *   is made, and if the last accordion is a list type accordion, then
 *   all accordions can close to form this summary view, and the ATC
 *   button changes to [blue]."
 *
 * Scope: only list-layout rows (Carat / Size / compound-accordion parts
 * — anything rendered by snippets/product-option-accordion.liquid with
 * layout: 'list', detectable at runtime via the
 * .product-option-accordion__body--list child, no markup changes
 * needed) trigger the auto-close-and-advance cascade. Swatch-layout
 * accordions (Metal, Stone, Shape) are untouched by the cascade itself
 * — Metal's standalone accordion (blocks/product-group-selector.liquid,
 * not part of <accordion-group>) is closed by the page-wide single-open
 * listener below, and swept closed again when the cascade completes.
 *
 * The completion signal (`pdp:selections-complete`) is one-way for the
 * rest of the page's life — reopening an accordion afterward to change
 * a value does not un-fire it (assets/product-purchase-form.js and
 * assets/sticky-add-to-cart.js both listen for it to flip their ATC
 * button to the "ready" blue state via a `.is-ready` class).
 */

let selectionsComplete = false;
// <accordion-group> whose last list pick is waiting on required gem fields.
let cascadeEndGroup = null;
// Arrow keys move a radio group's checked value (firing `change`) while the
// shopper is still browsing it, so an arrow-driven change must not close the
// row. Space and pointer picks still advance.
let arrowNavigation = false;
// Radio Space confirmed on keyup whose default action is about to check it.
let spaceConfirmInput = null;

function isListLayout(details) {
  return !!details.querySelector('.product-option-accordion__body--list');
}

/**
 * Next configurator accordion after `details` in document order within the
 * same <accordion-group>. Each <details> sits alone inside its own
 * <accordion-custom> wrapper, so siblings can't be walked directly. Rows
 * hidden by an ancestor (e.g. a ring-size host with no sizes for the
 * current variant) render no boxes and are skipped.
 */
function nextDetailsInGroup(details) {
  const group = details.closest('accordion-group');
  if (!group) return null;
  const all = Array.from(group.querySelectorAll('details.product-option-accordion'));
  const index = all.indexOf(details);
  if (index === -1) return null;
  return all.slice(index + 1).find((el) => el.getClientRects().length > 0) || null;
}

/**
 * Required gem fields (snippets/product-gem-fields.liquid) are swatch rows
 * outside the cascade; completion waits until each one in the group has a pick.
 */
function requiredGemFieldsSatisfied(group) {
  if (!group) return true;
  return Array.from(group.querySelectorAll('[data-gem-field-required="true"]')).every(
    (host) => !!host.querySelector('input[data-gem-field-input]:checked')
  );
}

/**
 * Only one configurator accordion open at a time — including Metal,
 * which lives outside the purchase-form <accordion-group>. Closing
 * (open → false) is always allowed so the final summary view can
 * collapse every row.
 */
function handleToggle(event) {
  const details = event.target;
  if (!(details instanceof HTMLDetailsElement)) return;
  if (!details.classList.contains('product-option-accordion')) return;
  if (!details.open) return;

  document.querySelectorAll('details.product-option-accordion').forEach((other) => {
    if (other !== details && other.open) other.open = false;
  });
}

function completeSelections() {
  if (selectionsComplete) return;
  selectionsComplete = true;
  document
    .querySelectorAll('details.product-option-accordion')
    .forEach((details) => {
      details.open = false;
    });
  // Stop Metal's open-by-default from re-expanding on breakpoint changes
  // after the summary view is reached.
  document.querySelectorAll('accordion-custom').forEach((el) => {
    if (!el.querySelector('details.product-option-accordion')) return;
    el.removeAttribute('open-by-default-on-desktop');
    el.removeAttribute('open-by-default-on-mobile');
  });
  document.dispatchEvent(new CustomEvent('pdp:selections-complete'));
}

function isConfiguratorInput(el) {
  return !!el?.classList?.contains('product-option-accordion__input');
}

function handleChange(event) {
  if (selectionsComplete || arrowNavigation) return;
  const input = event.target;
  if (!isConfiguratorInput(input) || !input.checked) return;
  advance(input, input === spaceConfirmInput);
}

function advance(input, keyboard) {
  // The list cascade already reached its end but was waiting on a required
  // gem field; a gem pick that satisfies them completes it.
  if (cascadeEndGroup && input.hasAttribute('data-gem-field-input')) {
    if (requiredGemFieldsSatisfied(cascadeEndGroup)) finish(input, keyboard);
    return;
  }

  const details = input.closest('details.product-option-accordion');
  if (!details || !isListLayout(details)) return;

  details.open = false;

  const next = nextDetailsInGroup(details);
  if (next) {
    next.open = true;
    // Programmatic opens skip :focus-within, which gates the open/close
    // transition in accordion-custom-component.liquid — focus the summary
    // so the cascade animates instead of snapping open.
    next.querySelector(':scope > summary')?.focus({ preventScroll: true });
  } else {
    const group = details.closest('accordion-group');
    if (requiredGemFieldsSatisfied(group)) {
      finish(input, keyboard);
    } else {
      cascadeEndGroup = group;
    }
  }
}

/**
 * Completion closes every row, including the one holding focus, which would
 * drop a keyboard shopper back to <body>. Keep them on that row's summary.
 * Pointer picks leave focus where the browser put it.
 */
function finish(input, keyboard) {
  completeSelections();
  if (keyboard) {
    input.closest('details.product-option-accordion')?.querySelector(':scope > summary')?.focus();
  }
}

function handleKeydown(event) {
  if (!/^Arrow(Up|Down|Left|Right)$/.test(event.key)) return;
  if (!isConfiguratorInput(event.target)) return;
  // The radio's `change` is dispatched by this keydown's default action, in
  // the same task; clear the flag once that task has finished.
  arrowNavigation = true;
  setTimeout(() => {
    arrowNavigation = false;
  }, 0);
}

/**
 * Space confirms the radio it is on. On an unchecked radio, this keyup's
 * default action (after this listener, in Chromium and Safari) checks it and
 * fires `change`, which advances. On an already-checked one (e.g. reached
 * with the arrow keys) no `change` follows, so advance here. A row that is
 * no longer open has already advanced, so a pick never advances twice.
 */
function handleKeyup(event) {
  if (event.key !== ' ' || selectionsComplete) return;
  const input = event.target;
  if (!isConfiguratorInput(input) || input.type !== 'radio') return;
  if (!input.checked) {
    spaceConfirmInput = input;
    setTimeout(() => {
      spaceConfirmInput = null;
    }, 0);
    return;
  }
  if (!input.closest('details.product-option-accordion')?.open) return;
  advance(input, true);
}

document.addEventListener('toggle', handleToggle, true);
document.addEventListener('keydown', handleKeydown, true);
document.addEventListener('keyup', handleKeyup, true);
document.addEventListener('change', handleChange);
